// src/components/SessionNotesView.tsx
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MultiSearchInput from "@/components/diagnosis/MultiSearchInput";

// ✅ Import your diagnosis codes JSON
import diagnosisCodesData from "@/data/diagnosisCodes.json";

interface DiagnosisCode {
  code: string;
  description: string;
}

interface SessionNotesViewProps {
  sections: {
    subjective?: { content: string } | string;
    objective?: { content: string } | string;
    assessment?: { content: string } | string;
    plan?: { content: string } | string;
    diagnosis?: { content: string } | string;
    [key: string]: any;
  };
  editable?: boolean;
  onDiagnosisChange?: (codes: string[]) => void;
}

const extractContent = (section: any): string | undefined => {
  if (typeof section === 'string') return section;
  if (section && typeof section === 'object' && 'content' in section) {
    return section.content;
  }
  return undefined;
};

const SectionCard: React.FC<{ title: string; content?: string }> = ({ title, content }) => {
  return (
    <Card className="border border-gray-200">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-800">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {content && content.trim() !== "" ? (
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {content}
          </p>
        ) : (
          <p className="text-gray-400 italic">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

const SessionNotesView: React.FC<SessionNotesViewProps> = ({
  sections,
  editable = false,
  onDiagnosisChange,
}) => {
  // ✅ Format diagnosis suggestions once (static, so safe to do in component body)
  const diagnosisSuggestions = React.useMemo(() => {
    return (diagnosisCodesData as DiagnosisCode[]).map(
      (item) => `${item.code} - ${item.description}`
    );
  }, []);

  const sectionList = [
    { key: 'subjective', title: 'Subjective' },
    { key: 'objective', title: 'Objective' },
    { key: 'assessment', title: 'Assessment' },
    { key: 'plan', title: 'Plan' },
  ];

  const diagnosisContent = extractContent(sections.diagnosis);
  const diagnosisCodes = diagnosisContent
    ? diagnosisContent
        .split(',')
        .map(code => code.trim())
        .filter(code => code.length > 0)
    : [];

  return (
    <div className="space-y-6">
      {sectionList.map(({ key, title }) => {
        const content = extractContent(sections[key as keyof typeof sections]);
        return <SectionCard key={key} title={title} content={content} />;
      })}

      {/* Diagnosis Section */}
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-800">Diagnosis Codes</CardTitle>
        </CardHeader>
        <CardContent>
          {editable ? (
            <MultiSearchInput
              selectedItems={diagnosisCodes}
              onChange={(newCodes) => {
                if (onDiagnosisChange) {
                  onDiagnosisChange(newCodes);
                }
              }}
              searchSuggestions={diagnosisSuggestions}
              placeholder="Search diagnosis codes (e.g., F32.9)..."
            />
          ) : diagnosisCodes.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {diagnosisCodes.map((code, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-blue-100 text-blue-800 text-sm font-mono px-2 py-1 rounded"
                >
                  {code}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 italic">No diagnosis codes available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionNotesView;